import MultiSelectInput, { MultiSelectInputPropsType } from "components/common/form/inputs/MultiSelectInput"
import { uuid } from "config/app-constants"
import { graph } from "config/axios-context"
import { DocumentNode } from "graphql"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { IdName, IdNameType } from "types/common-types"
import { InputCostumeErrorType } from "types/form-types"
import { FruitCategoriesIdNameQuery, FruitCategoriesIdNameQueryVariables, FruitGroupsIdNameQuery, FruitGroupsIdNameQueryVariables } from "types/graphql"
import useAppContext from "utilities/hooks/useAppContext"

const FRUIT_CLASSIFICATION_PLEASE_SELECT_ID = -1;
const FRUIT_CLASSIFICATION_SELECT_ALL = -2;

export type ClassificationType = IdNameType<string | number, string> & {
    classificationId: number,
}

type QueryType = FruitCategoriesIdNameQuery | FruitGroupsIdNameQuery
type QueryVariables = FruitCategoriesIdNameQueryVariables | FruitGroupsIdNameQueryVariables

export type FruitClassificationMultiselectInputPropsType = {
    fruitId: string | undefined | null | number | false,
    value?: ClassificationType[],
    onChange?: (s: ClassificationType[] | undefined, event: React.MouseEvent | React.KeyboardEvent | React.FocusEvent) => void,
    min?: number,
    max?: number,
    customErrors?: InputCostumeErrorType<ClassificationType[], HTMLInputElement>[],
    required?: boolean
    name: string
    label: string,
    queryData: {
        query: DocumentNode,
        field: 'fruitCategories' | 'fruitGroups'
    }
}

const FruitClassificationMultiselectInput = (props: FruitClassificationMultiselectInputPropsType) => {
    const [t] = useTranslation();
    const context = useAppContext();
    const [defineValues] = useState<ClassificationType[]>(props.value || []);
    let fruitClassificationSelectOptions: ClassificationType[] = [];

    const fruitClassificationMultiselectSettings: MultiSelectInputPropsType<ClassificationType> = {
        dataSource: {
            request: async () => {
                if (!props.fruitId) {
                    fruitClassificationSelectOptions = [];
                    return [{ 
                        id: FRUIT_CLASSIFICATION_PLEASE_SELECT_ID, 
                        name: t('form.agriculture.sale.error.fruitCategoryInput'),
                        classificationId: 0
                    }]
                }

                const request = await graph<QueryType, QueryVariables>({
                    query: props.queryData.query,
                    variables: {
                        where: {
                            fruitId: {
                                eq: Number(props.fruitId)
                            }
                        }
                    }
                })

                if (!request.success || request.data === undefined) {
                    context.notify('error', 'error.loading-data');
                    throw request.errors;
                }

                const data = (request.data as any)[props.queryData.field]?.items as IdName[];

                let list: ClassificationType[] = data.map(item => {
                    return {
                        id: defineValues.find(i => i.classificationId === item.id)?.id || uuid(),
                        classificationId: item.id,
                        name: item.name
                    } as ClassificationType;
                });
                if(list?.length) {
                    fruitClassificationSelectOptions = [...list];
                    list = [{
                        id: FRUIT_CLASSIFICATION_SELECT_ALL,
                        classificationId: 0,
                        name: `-- ${t('common.select-all')}`
                    }, ...list];
                }

                return list;
            }
        },
        getItemId: i => i.id,
        view: {
            inputView: i => i?.name
        },
        label: props.label,
        name: props.label,
        onChange: (v, e) => {
            if (v?.length) {
                v = v.filter(i => i.id !== FRUIT_CLASSIFICATION_PLEASE_SELECT_ID);
                if (v.some(i => i.id === FRUIT_CLASSIFICATION_SELECT_ALL)) {
                    v = fruitClassificationSelectOptions;
                }
            }
            if (props.onChange) props.onChange(v, e);
        },
        customErrors: [
            ...props.customErrors || [],
            {
                message: 'InvalidInput',
                name: 'InvalidInput',
                validate(v) {
                    return !v?.length || v.every(c => c.classificationId > 0) ? true : false;
                },
            }
        ],
        max: props.max,
        min: props.min,
        required: props.required,
        checkIfItemExists(source, destination) {
            return source?.classificationId === destination?.classificationId;
        },
    };

    return (
        <MultiSelectInput
            {...fruitClassificationMultiselectSettings}
            value={props.value}
        />
    );
};

export default FruitClassificationMultiselectInput;
